// 方案與帳單（plan/vms-web-v0.md §5.17 BILL；§5.9 CLOUD-01/02/04/05/07；§5.10 AI-01 的購買部分）。
//   - 付費主體 = 頁首範圍（SPEC #vms-web-835、#vms-web-220「組織管理頁與範圍」；VMS UX 評審 2026-10-13 P0-3）：範圍是組織 → 那個組織；
//     範圍是個人組織的家戶 → 那個家戶；頁內不另放主體下拉——標題下一個膠囊「付費主體：X ▾」，切換 = 切頁首範圍並提示；
//     範圍是「全部」等對不到主體、而主體不只一個時先問「要看哪一個的方案與帳單？」
//   - 可購商品（價格暫定時標出）→ Stripe Checkout → 回來輪詢到 webhook 投影完成
//   - 訂閱（狀態、續訂日、Customer Portal）、席次（來源 / 狀態 / 指派設備 / 本月上傳量）、合約到期提醒、雲端 AI 額度
// 看：主體成員；買：org.billing / 家戶 owner；指派：org.billing / 家戶 owner、admin（伺服器端再驗一次）。
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { KinioError } from '@kinio/web-sdk';
import { useScope } from '../../lib/scope';
import { homePlanSentence, useHomePlanCloud } from './homePlanCloud';
import { useDevices } from '../../lib/useDevices';
import { fullName } from '../../lib/devices';
import { billingApi, hasOrgPerm, type BillingOverview, type BillingSeat, type BillingSubjectRef, type VmsProduct } from '../../lib/planeApi';
import { productComingSoon } from './comingSoon';
import { dateStamp } from '../../lib/timefmt';


interface SubjectOption extends BillingSubjectRef {
  name: string;
  canBuy: boolean;
  canAssign: boolean;
}

/** 從總覽「雲錄席次」來（/billing#seats，B-30）：席次區塊畫出來時捲過去。 */
const scrollIfSeats = (el: HTMLElement | null) => {
  if (el && window.location.hash === '#seats') el.scrollIntoView?.({ block: 'start' });
};

export function BillingPage() {
  const { t } = useTranslation();
  const { orgs, homes, org, scope, setScope } = useScope();
  const subjects = useMemo<SubjectOption[]>(() => {
    // 組織政策這次登入不符（要求 MFA…）的組織不列：它的 API 會擋
    const out: SubjectOption[] = orgs.filter((o) => !o.notice).map((o) => ({
      api: o.api,
      kind: 'org',
      id: o.org_id,
      name: o.display_name,
      canBuy: hasOrgPerm(o, 'org.billing'),
      canAssign: hasOrgPerm(o, 'org.billing'),
    }));
    for (const h of homes) {
      if (h.org_kind === 'team' || !h.role) continue;
      out.push({
        api: h.endpoints.api,
        kind: 'home',
        id: h.home_id,
        name: h.display_name || h.home_id,
        canBuy: h.role === 'owner',
        canAssign: h.role === 'owner' || h.role === 'admin',
      });
    }
    return out;
  }, [orgs, homes]);
  const key = subjectKeyOf(scope, org?.org_id, subjects);
  const subject = subjects.find((s) => `${s.kind}:${s.id}` === key) ?? null;
  const label = (s: SubjectOption) => (s.kind === 'org' ? t('billing.subject_org', { name: s.name }) : t('billing.subject_home', { name: s.name }));
  const pick = (s: SubjectOption) => setScope(`${s.kind}:${s.id}`, '', { announce: t('scope.switched_to', { name: s.name }) });
  return (
    <div className="page">
      <h1 className="page-title">{t('nav.billing')}</h1>
      {!subject && subjects.length > 0 ? (
        <section className="org-chooser" data-testid="billing-chooser">
          <h2 className="section-title">{t('billing.pick_title')}</h2>
          <p className="muted small">{t('orggate.pick_body')}</p>
          <div className="org-cards">
            {subjects.map((s) => (
              <button key={`${s.kind}:${s.id}`} type="button" className="card org-card" data-subject={`${s.kind}:${s.id}`} onClick={() => pick(s)}>
                <strong>{label(s)}</strong>
              </button>
            ))}
          </div>
        </section>
      ) : (
        subject && <SubjectCapsule current={subject} all={subjects} label={label} pick={pick} />
      )}
      {subject ? <SubjectBilling key={key} subject={subject} /> : subjects.length === 0 && <p className="card muted">{t('billing.no_subject')}</p>}
    </div>
  );
}

/** 頁首範圍 → 付費主體（範圍是家戶且是個人組織的家戶 → 家戶；否則範圍所在的組織）；對不到 → 只有一個主體就用它，不然 ''（先問）。 */
export function subjectKeyOf(scope: string, orgId: string | undefined, subjects: Pick<SubjectOption, 'kind' | 'id'>[]): string {
  const has = (k: string) => subjects.some((s) => `${s.kind}:${s.id}` === k);
  if (scope.startsWith('home:') && has(scope)) return scope;
  if (orgId && has('org:' + orgId)) return 'org:' + orgId;
  return subjects.length === 1 ? `${subjects[0].kind}:${subjects[0].id}` : '';
}

/** 標題下的膠囊「付費主體：X ▾」：選了 = 切頁首範圍（不是頁內另一個下拉）。 */
function SubjectCapsule({ current, all, label, pick }: { current: SubjectOption; all: SubjectOption[]; label(s: SubjectOption): string; pick(s: SubjectOption): void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const many = all.length > 1;
  return (
    <div className="org-capsule-wrap">
      <button type="button" className="org-capsule" data-testid="billing-capsule" disabled={!many} aria-haspopup={many ? 'listbox' : undefined} aria-expanded={many ? open : undefined} onClick={() => setOpen((o) => !o)}>
        {t('billing.capsule', { name: current.name })}
        {many && <span aria-hidden> ▾</span>}
      </button>
      {open && (
        <ul className="org-capsule-menu" role="listbox" aria-label={t('billing.pick_title')} onMouseLeave={() => setOpen(false)}>
          {all.map((s) => (
            <li key={`${s.kind}:${s.id}`} role="option" aria-selected={s.kind === current.kind && s.id === current.id}>
              <button type="button" onClick={() => (setOpen(false), s.id !== current.id && pick(s))}>
                {label(s)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function money(p: { unit_amount: number; currency: string }, qty = 1): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: p.currency.toUpperCase() }).format((p.unit_amount * qty) / 100);
  } catch {
    return `${((p.unit_amount * qty) / 100).toFixed(2)} ${p.currency.toUpperCase()}`;
  }
}

function errText(t: (k: string, o?: Record<string, unknown>) => string, e: unknown): string {
  if (e instanceof KinioError) return t(`billing.err_${e.code}`, { defaultValue: t('billing.err_generic', { code: e.code }) });
  return String(e);
}

function returnUrl(): string {
  const u = new URL(window.location.href);
  u.search = '';
  return u.toString();
}

function SubjectBilling({ subject }: { subject: SubjectOption }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const qkey = ['billing', subject.kind, subject.id];
  const q = useQuery({ queryKey: qkey, queryFn: () => billingApi.overview(subject), refetchInterval: 15_000 });
  const [params, setParams] = useSearchParams();
  const [notice, setNotice] = useState<{ kind: 'ok' | 'warn' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // 從 Stripe Checkout 回來：?checkout=done&intent=… → 輪詢到 webhook 投影完成
  const checkout = params.get('checkout');
  const intent = params.get('intent');
  useEffect(() => {
    if (!checkout || !intent) return;
    if (checkout === 'cancel') {
      setNotice({ kind: 'warn', text: t('billing.checkout_canceled') });
      setParams({}, { replace: true });
      return;
    }
    let stop = false;
    setNotice({ kind: 'warn', text: t('billing.checkout_processing') });
    const started = Date.now();
    const tick = async () => {
      if (stop) return;
      try {
        const r = await billingApi.checkoutStatus(subject, intent);
        if (r?.status === 'completed') {
          setNotice({ kind: 'ok', text: t('billing.checkout_done') });
          setParams({}, { replace: true });
          void qc.invalidateQueries({ queryKey: qkey });
          return;
        }
        if (r?.status === 'expired' || r?.status === 'failed') {
          setNotice({ kind: 'err', text: t('billing.checkout_failed') });
          setParams({}, { replace: true });
          return;
        }
      } catch {
        /* 主體不對（換了範圍）就讓它超時 */
      }
      if (Date.now() - started > 120_000) {
        setNotice({ kind: 'warn', text: t('billing.checkout_slow') });
        return;
      }
      setTimeout(tick, 1500);
    };
    void tick();
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout, intent, subject.id]);

  const buy = async (p: VmsProduct, qty: number) => {
    setBusy(p.product_id);
    setNotice(null);
    try {
      const r = await billingApi.checkout(subject, { product_id: p.product_id, quantity: qty, return_url: returnUrl() });
      if (r?.url) {
        window.location.assign(r.url); // 回來時網址帶 checkout=done&intent=…
        return;
      }
    } catch (e) {
      setNotice({ kind: 'err', text: errText(t, e) });
    }
    setBusy(null);
  };
  const portal = async () => {
    setBusy('portal');
    try {
      const r = await billingApi.portal(subject, returnUrl());
      if (r?.url) {
        window.location.assign(r.url);
        return;
      }
    } catch (e) {
      setNotice({ kind: 'err', text: errText(t, e) });
    }
    setBusy(null);
  };

  if (q.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (q.isError || !q.data) return <p className="error">{errText(t, q.error)}</p>;
  const d = q.data;
  const draft = d.products.some((p) => p.draft_values);
  return (
    <>
      {notice && (
        <p className={`card ${notice.kind === 'err' ? 'error' : ''}`} role="status" data-testid="billing-notice">
          <span className={`badge ${notice.kind === 'ok' ? 'badge-ok' : notice.kind === 'warn' ? 'badge-warn' : ''}`}>{t(`billing.notice_${notice.kind}`)}</span> {notice.text}
        </p>
      )}
      <Seats d={d} subject={subject} onChanged={() => qc.invalidateQueries({ queryKey: qkey })} setNotice={setNotice} />
      <AiSummary d={d} />
      <section className="card stack">
        <h2 className="section-title">{t('billing.subscriptions')}</h2>
        {d.subscriptions.length === 0 ? (
          <p className="muted">{t('billing.no_subscriptions')}</p>
        ) : (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>{t('billing.items')}</th>
                  <th>{t('billing.state')}</th>
                  <th>{t('billing.period_end')}</th>
                </tr>
              </thead>
              <tbody>
                {d.subscriptions.map((s) => (
                  <tr key={s.sub_id} data-testid="billing-sub">
                    <td>
                      {s.items.map((it) => {
                        const p = d.products.find((x) => x.product_id === it.product_id);
                        return (
                          <div key={it.product_id}>
                            {p ? productName(t, p) : it.product_id} × {it.quantity}
                          </div>
                        );
                      })}
                    </td>
                    <td>
                      <span className={`badge ${['active', 'cancel_pending'].includes(s.state) ? 'badge-ok' : s.state === 'grace' ? 'badge-warn' : ''}`}>
                        {t(`billing.state_${s.state}`)}
                      </span>
                      {s.state === 'grace' && s.grace_until && <div className="muted small">{t('billing.grace_until', { at: dateStamp(s.grace_until) })}</div>}
                    </td>
                    <td>{s.period_end ? dateStamp(s.period_end) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {subject.canBuy && d.subscriptions.length > 0 && (
          <div>
            <button className="btn" disabled={busy === 'portal'} onClick={() => void portal()}>
              {t('billing.manage')}
            </button>{' '}
            <span className="muted small">{t('billing.manage_hint')}</span>
          </div>
        )}
      </section>
      <section className="card stack">
        <h2 className="section-title">
          {t('billing.buy')} {draft && <span className="badge badge-warn">{t('billing.draft_prices')}</span>}
        </h2>
        {!d.store_configured && <p className="muted">{t('billing.store_not_configured')}</p>}
        {!subject.canBuy && <p className="muted">{t(subject.kind === 'org' ? 'billing.need_org_billing' : 'billing.need_owner')}</p>}
        <Products d={d} canBuy={subject.canBuy && d.store_configured} busy={busy} onBuy={buy} />
      </section>
      <Archives d={d} />
    </>
  );
}

const ARCHIVE_SIZES = ['5gb', '20gb', '50gb'] as const;
const ARCHIVE_PERIODS = ['90d', '1y', '3y'] as const;

/** 證據包長期保管（#archives，SPEC #vms-web-export-archive）：9 格價格表 + 保管中的包；購買入口在匯出頁每個包的〔長期保管…〕。 */
function Archives({ d }: { d: BillingOverview }) {
  const { t } = useTranslation();
  const prods = d.products.filter((p) => p.kind === 'export_archive');
  const archives = d.archives ?? [];
  if (prods.length === 0 && archives.length === 0) return null;
  const cell = (size: string, period: string) => {
    const p = prods.find((x) => x.sku === `archive.${size}.${period}`);
    return p ? money(p) : '—';
  };
  return (
    <section className="card stack" id="archives" data-testid="billing-archives">
      <h2 className="section-title">{t('billing.archives')}</h2>
      <p className="muted small">{t('billing.archives_help')}</p>
      {prods.length > 0 && (
        <div className="table-wrap">
          <table className="grid" data-testid="archive-prices">
            <thead>
              <tr>
                <th>{t('billing.archive_size')}</th>
                {ARCHIVE_PERIODS.map((p) => (
                  <th key={p}>{t(`exports.archive_p_${p}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ARCHIVE_SIZES.map((sz) => (
                <tr key={sz}>
                  <td>{t(`exports.archive_size_${sz}`)}</td>
                  {ARCHIVE_PERIODS.map((p) => (
                    <td key={p}>{cell(sz, p)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {archives.length === 0 ? (
        <p className="muted">{t('billing.no_archives')}</p>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>{t('billing.archive_job')}</th>
                <th>{t('billing.archive_size')}</th>
                <th>{t('billing.archive_period')}</th>
                <th>{t('billing.state')}</th>
                <th>{t('billing.archive_ends')}</th>
              </tr>
            </thead>
            <tbody>
              {archives.map((a) => (
                <tr key={a.archive_id} data-testid="billing-archive">
                  <td>
                    <span className="mono small">{a.job_id}</span>
                    <div className="muted small">{a.reason}</div>
                  </td>
                  <td>{t(`exports.archive_size_${a.size_class}`)}</td>
                  <td>{t('billing.archive_days', { n: a.period_days })}</td>
                  <td>
                    <span className={`badge ${a.status === 'active' ? 'badge-ok' : 'badge-warn'}`}>{t(`billing.archive_st_${a.status}`)}</span>
                  </td>
                  <td>{a.ends_at ? dateStamp(a.ends_at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function productName(t: (k: string, o?: Record<string, unknown>) => string, p: VmsProduct): string {
  if (p.display_name) return p.display_name;
  if (p.kind === 'seat') return t('billing.seat_name', { mode: t(`billing.mode_${p.mode}`), days: p.retention_days });
  if (p.kind === 'ai') return t('billing.ai_name', { n: p.analyses });
  return t('billing.pack_name', { n: p.analyses });
}

function Products({ d, canBuy, busy, onBuy }: { d: BillingOverview; canBuy: boolean; busy: string | null; onBuy: (p: VmsProduct, qty: number) => void }) {
  const { t } = useTranslation();
  const [qty, setQty] = useState<Record<string, number>>({});
  const seats = d.products.filter((p) => p.kind === 'seat').sort((a, b) => (a.mode ?? '').localeCompare(b.mode ?? '') || (a.retention_days ?? 0) - (b.retention_days ?? 0));
  const ai = d.products.filter((p) => p.kind !== 'seat' && p.kind !== 'export_archive'); // 長期保管在匯出頁買（#archives 只列）
  const hasAiSub = d.subscriptions.some((s) => ['pending', 'active', 'cancel_pending', 'grace', 'on_hold'].includes(s.state) && s.items.some((i) => i.kind === 'ai'));
  return (
    <div className="table-wrap">
      <table className="grid">
        <thead>
          <tr>
            <th>{t('billing.product')}</th>
            <th>{t('billing.price')}</th>
            <th>{t('billing.quantity')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {[...seats, ...ai].map((p) => {
            const n = qty[p.product_id] ?? 1;
            const maxQ = p.kind === 'seat' ? 500 : p.kind === 'ai_pack' ? 20 : 1;
            const blocked = p.kind === 'ai' && hasAiSub;
            const soon = productComingSoon(p, d);
            return (
              <tr key={p.product_id} data-testid={`product-${p.sku}`} className={soon ? 'muted' : undefined}>
                <td>
                  {productName(t, p)}
                  {p.kind === 'ai' && p.features.length > 0 && <div className="muted small">{p.features.map((f) => t(`billing.ai_feature_${f}`, { defaultValue: f })).join(' · ')}</div>}
                  {p.kind === 'ai_pack' && <div className="muted small">{t('billing.pack_hint')}</div>}
                </td>
                <td>
                  {money(p)}
                  {p.interval && <span className="muted small"> / {t(`billing.per_${p.interval}`)}</span>}
                  {p.kind === 'seat' && <div className="muted small">{t('billing.per_seat')}</div>}
                </td>
                <td>
                  {maxQ > 1 ? (
                    <input
                      className="input"
                      type="number"
                      min={1}
                      max={maxQ}
                      style={{ width: 80 }}
                      value={n}
                      aria-label={t('billing.quantity')}
                      onChange={(e) => setQty({ ...qty, [p.product_id]: Math.max(1, Math.min(maxQ, Number(e.target.value) || 1)) })}
                    />
                  ) : (
                    '1'
                  )}
                </td>
                <td>
                  {soon ? (
                    <span className="badge" data-coming-soon="true">
                      {t('billing.coming_soon')}
                    </span>
                  ) : blocked ? (
                    <span className="muted small">{t('billing.ai_already')}</span>
                  ) : (
                    <button className="btn btn-primary btn-sm" disabled={!canBuy || busy !== null} onClick={() => onBuy(p, n)}>
                      {t('billing.buy_btn', { total: money(p, n) })}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Seats({
  d,
  subject,
  onChanged,
  setNotice,
}: {
  d: BillingOverview;
  subject: SubjectOption;
  onChanged: () => void;
  setNotice: (n: { kind: 'ok' | 'warn' | 'err'; text: string } | null) => void;
}) {
  const { t } = useTranslation();
  const { cameras, tree } = useDevices();
  const homes = new Set(d.homes);
  // 可指派的：主體底下家戶的攝影機與通道
  const candidates = cameras.filter((c) => homes.has(c.homeId));
  const taken = new Map(d.seats.filter((s) => s.assigned_ref).map((s) => [s.assigned_ref!, s.seat_id]));
  const [busy, setBusy] = useState<string | null>(null);
  const name = (ref?: string, homeId?: string) => {
    if (!ref) return '';
    const c = cameras.find((x) => x.aid === ref && (!homeId || x.homeId === homeId));
    return c ? fullName(c, tree) : ref;
  };
  const assign = async (s: BillingSeat, ref: string) => {
    setBusy(s.seat_id);
    try {
      if (ref === '') await billingApi.unassign(subject, s.seat_id);
      else {
        const r = await billingApi.assign(subject, s.seat_id, ref);
        setNotice(
          r?.covered_by_plan
            ? { kind: 'warn', text: t('billing.covered_by_plan', { name: name(ref) }) }
            : { kind: 'ok', text: t('billing.assigned', { name: name(ref), within: 60 }) },
        );
      }
      onChanged();
    } catch (e) {
      setNotice({ kind: 'err', text: errText(t, e) });
    }
    setBusy(null);
  };
  const soon = (until?: string) => !!until && new Date(until).getTime() - Date.now() < 30 * 86400_000;
  // CLOUD-03：全時席次的實測上行（最近 1 小時），依家戶加總
  const upByHome = new Map<string, number>();
  for (const s of d.seats) {
    if (s.mode === 'continuous' && s.assigned_home_id && s.upload_kbps_last_hour !== undefined)
      upByHome.set(s.assigned_home_id, (upByHome.get(s.assigned_home_id) ?? 0) + s.upload_kbps_last_hour);
  }
  const homeName = (id: string) => cameras.find((c) => c.homeId === id)?.homeName ?? id;
  const free = d.seats.filter((s) => s.authorizing && !s.assigned_ref).length;
  // 席次以外由家戶方案提供雲錄的攝影機（第二輪 R2-P1-7：總覽、帳單、攝影機錄影分頁三處各講一半 → 這裡講一次）
  const hp = useHomePlanCloud(candidates.filter((c) => !taken.has(c.aid)));
  const hpText = homePlanSentence(t, hp.list, 5, (d) => fullName(d, tree));
  return (
    <section className="card stack" id="seats" ref={scrollIfSeats}>
      <h2 className="section-title">
        {t('billing.seats')} <span className="muted small">{t('billing.seats_summary', { total: d.seats.length, free })}</span>
      </h2>
      {d.seats.length === 0 ? (
        <p className="muted">{t('billing.no_seats')}</p>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>{t('billing.seat')}</th>
                <th>{t('billing.source')}</th>
                <th>{t('billing.state')}</th>
                <th>{t('billing.assigned_to')}</th>
                <th>{t('billing.usage_month')}</th>
              </tr>
            </thead>
            <tbody>
              {d.seats.map((s) => (
                <tr key={s.seat_id} data-testid="billing-seat">
                  <td>
                    {t(`billing.mode_${s.mode}`)} · {t('billing.days', { count: s.retention_days })}
                  </td>
                  <td>
                    {t(`billing.source_${s.source}`)}
                    {s.source === 'grant' && s.source_until && (
                      <div className={`small ${soon(s.source_until) ? 'error' : 'muted'}`}>{t('billing.contract_until', { at: dateStamp(s.source_until) })}</div>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${s.authorizing ? (s.status === 'active' ? 'badge-ok' : 'badge-warn') : ''}`}>{t(`billing.state_${s.status}`, { defaultValue: t('common.state_unknown') })}</span>
                  </td>
                  <td>
                    {subject.canAssign ? (
                      <select
                        className="input"
                        style={{ minWidth: 200 }}
                        value={s.assigned_ref ?? ''}
                        disabled={busy === s.seat_id}
                        aria-label={t('billing.assigned_to')}
                        onChange={(e) => void assign(s, e.target.value)}
                      >
                        <option value="">{t('billing.unassigned')}</option>
                        {s.assigned_ref && !candidates.some((c) => c.aid === s.assigned_ref) && <option value={s.assigned_ref}>{s.assigned_ref}</option>}
                        {candidates.map((c) => (
                          <option key={c.key} value={c.aid} disabled={taken.has(c.aid) && taken.get(c.aid) !== s.seat_id}>
                            {fullName(c, tree)}
                            {d.homes.length > 1 ? ` — ${c.homeName}` : ''}
                            {taken.has(c.aid) && taken.get(c.aid) !== s.seat_id ? ` (${t('billing.has_seat')})` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      name(s.assigned_ref, s.assigned_home_id) || <span className="muted">{t('billing.unassigned')}</span>
                    )}
                  </td>
                  <td className="small">
                    {s.usage_this_month
                      ? t('billing.usage', { clips: s.usage_this_month.clips, mb: (s.usage_this_month.bytes / 1e6).toFixed(1) })
                      : '—'}
                    {s.upload_kbps_last_hour !== undefined && <div className="muted">{t('billing.rate', { kbps: s.upload_kbps_last_hour })}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {upByHome.size > 0 && (
        <div className="small" data-testid="billing-uplink">
          {t('billing.uplink_title')}
          {[...upByHome].map(([h, kbps]) => (
            <div key={h}>
              {homeName(h)}：{t('billing.uplink_home', { mbps: (kbps / 1000).toFixed(2) })}
            </div>
          ))}
        </div>
      )}
      {hpText && (
        <p className="small" data-testid="billing-home-plan">
          {hpText}
        </p>
      )}
      <p className="muted small">{t('billing.seats_hint')}</p>
    </section>
  );
}

function AiSummary({ d }: { d: BillingOverview }) {
  const { t } = useTranslation();
  const ai = d.ai;
  if (!ai.active) return null;
  const pct = ai.limit > 0 ? Math.min(100, Math.round((ai.used * 100) / ai.limit)) : 0;
  return (
    <section className="card stack" data-testid="billing-ai">
      <h2 className="section-title">{t('billing.ai')}</h2>
      <div>
        {t('billing.ai_usage', { used: ai.used, limit: ai.limit, period: ai.period })}
        {ai.packs > 0 && <span className="muted small"> {t('billing.ai_packs', { n: ai.packs })}</span>}
      </div>
      <div className="meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
        <div className={`meter-fill ${pct >= 100 ? 'err' : pct >= 80 ? 'warn' : ''}`} style={{ width: `${pct}%` }} />
      </div>
      {pct >= 100 ? <p className="error small">{t('billing.ai_limit_reached')}</p> : pct >= 80 ? <p className="small">{t('billing.ai_near_limit')}</p> : null}
      <p className="muted small">{ai.features.map((f) => t(`billing.ai_feature_${f}`, { defaultValue: f })).join(' · ')}</p>
    </section>
  );
}
